import { isSupportedRegion, type SupportedRegion } from '../regions.ts';

export type PentestConfig = Partial<Record<SupportedRegion, string>>;

export type PentestSession = {
  username: string;
  upToken: string;
  region: SupportedRegion;
};

export type PentestLoginResult =
  | { status: 'success'; username: string; upToken: string }
  | { status: 'invalid' }
  | { status: 'rate_limited'; retryAfterSeconds?: number }
  | { status: 'error' };

const SESSION_STORAGE_KEY = 'pentest.session';

export const parsePentestConfig = (encoded: string | undefined): PentestConfig => {
  if (!encoded) return {};
  try {
    const parsed: unknown = JSON.parse(atob(encoded));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const config: PentestConfig = {};
    for (const [region, entry] of Object.entries(parsed)) {
      const url = (entry as { chatStreamApiUrl?: unknown } | null)?.chatStreamApiUrl;
      if (isSupportedRegion(region) && typeof url === 'string' && URL.canParse(url.trim())) {
        config[region] = new URL(url.trim()).origin;
      }
    }
    return config;
  } catch {
    return {};
  }
};

export const pentestLogin = async (
  baseUrl: string,
  username: string,
  password: string,
  fetchImpl: typeof fetch = fetch
): Promise<PentestLoginResult> => {
  try {
    const response = await fetchImpl(`${baseUrl}/api/v1/auth/pentest/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (response.status === 401) return { status: 'invalid' };
    if (response.status === 429) {
      const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
      return {
        status: 'rate_limited',
        retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : undefined,
      };
    }
    if (!response.ok) return { status: 'error' };

    const body: unknown = await response.json();
    const { username: canonical, up_token: upToken } = (body ?? {}) as Record<string, unknown>;
    if (typeof canonical !== 'string' || typeof upToken !== 'string' || !upToken) {
      return { status: 'error' };
    }
    return { status: 'success', username: canonical, upToken };
  } catch {
    return { status: 'error' };
  }
};

type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const saveSession = (
  session: PentestSession,
  storage: SessionStorageLike = sessionStorage
): void => {
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable: the session just won't survive a reload.
  }
};

export const readSession = (storage: SessionStorageLike = sessionStorage): PentestSession | null => {
  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const { username, upToken, region } = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof username !== 'string' ||
      typeof upToken !== 'string' ||
      !upToken ||
      typeof region !== 'string' ||
      !isSupportedRegion(region)
    ) {
      return null;
    }
    return { username, upToken, region };
  } catch {
    return null;
  }
};

export const clearSession = (storage: SessionStorageLike = sessionStorage): void => {
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
};
